import React from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router-dom';

import { formatCaseStatus, formatDate, formatNull, isDatePastInMoscow } from '../../Utils/GlobalUtils';
import { useTimeModal } from '../TimeModal/TimeModalContext';

import CaseApplicants from '../CaseComponents/CaseApplicants';
import CaseDebtors from '../CaseComponents/CaseDebtors';
import AMPanel from '../CaseComponents/AMPanel';
import FilesMiniTable from '../FilesMiniTable';
import FileUploadField from '../FileUploadField';
import CommentsCase from '../CaseComponents/CommentsCase';

import Loading from '../Loading';
import LoadingBlock from '../LoadingBlock';
import WarningPanel from '../WarningPanel';

import IconContainer from '../../Components/IconContainer';
import { ReactComponent as PenIcon } from '../../icons/pen.svg';

export default function MainInfo({ caseId }) {
    const navigate = useNavigate();
    const location = useLocation();

    const { openTimeModal } = useTimeModal();

    const [formInfo, setFormInfo] = React.useState({
        applicants: [],
        files: []
    });

    const [reload, setReload] = React.useState(true);
    const [loading, setLoading] = React.useState(true);
    const [warning, setWarning] = React.useState('');

    const [isAdmin, setIsAdmin] = React.useState(false);
        
    React.useEffect(() => {
        const token = localStorage.getItem('token');
    
        try {
            const decodedToken = jwtDecode(token);
            if (decodedToken.role==='АппаратСРО') {
                setIsAdmin(true);
            } else {
                setIsAdmin(false);
            }
        } catch (error) {
            console.error('Ошибка при декодировании токена: ', error);
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        axios.get(`/api/get-full-info-case?id=${caseId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        })
        .then((response) => {
            setWarning('');
            setFormInfo((prev) => ({
                ...prev,
                ...response.data,
            }));
            document.title = `${response.data.number || response.data.internal_number} — Личный кабинет Альянс`;
        })
        .catch((error) => {
            if (error.response?.data.action == 'logout') window.location.href = '/auth/logout';
            setFormInfo([]);
            setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
        })
        .finally(() => {
            setLoading(false);
            setReload(false);
        })
    }, [reload, caseId]);

    const [access, setAccess] = React.useState(false);
    React.useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) handleLogout();

        axios.get('/api/get-my-user-role', {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then(response => {
            const hasAccess = response.data.role === 'АппаратСРО';
            setAccess(hasAccess);
        }).catch(err => {
            console.error(`Ошибка получения данных: ${err}`);
        })
    }, []);

    const [files, setFiles] = React.useState({
        caseFile: null,
    });
    const handleFileChange = (field) => (e) => {
        const file = e.target.files[0];
        setFiles((prev) => ({
          ...prev,
          [field]: file || null,
        }));

        const formData = new FormData();
        formData.append('case_id', caseId);
        formData.append('file_caseFile', file);

        axios.put('/api/add-case-file', formData, {
            headers: { 
                'Content-Type': 'multipart/form-data',
            },
        }).then(() => {
            setReload(true);
            setLoading(true);
            setFiles((prev) => ({
                ...prev,
                [field]: null,
            }));
        }).catch((error) => {
            console.error(error);
            openTimeModal(`Файл не отправлен. ${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
        });
    };
    
    const handleFileReset = (field) => () => {
        setFiles((prev) => ({
          ...prev,
          [field]: null,
        }));
    };

    const handleDeleteFile = (id) => {
        const token = localStorage.getItem('token');

        setLoading(true);

        axios.delete('/api/delete-case-file', {
            data: { 
                fileId: id,
                caseId: caseId, 
            },
            headers: {
                Authorization: `Bearer ${token}`,
            }
            })
        .then(() => {
            openTimeModal('Файл успешно удален', 'success');
        })
        .catch((error) => {
            openTimeModal(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`, 'error');
        })
        .finally(() => {
            setReload(true);
        });
    };

    const [preTypeComment, setPreTypeComment] = React.useState('');
    const [preContentComment, setPreContentComment] = React.useState('');

    return (
    <>
    <div className='pageLimiter'>
        {(!formInfo.internal_number && loading) ? <Loading />
        : warning ? <WarningPanel text={warning} />
        :
        <>
            <div className='iconTopRight'>
                <button
                    type='button'
                    className='miniButton'
                    onClick={() => navigate(`/change-case/${formInfo.id}`)}
                >
                    <IconContainer
                        Icon={PenIcon}
                        isClickable={false}
                        size='16'
                        color='blue'
                    />
                    Изменить данные
                </button>
            </div>

            <h3 className='formHeadline'>Основная информация</h3>
            <div className='labelInputWrapper'>
                <a className='label'>Арбитражный суд</a>
                <div className='labelInputRight'>
                    <a>{formatNull(formInfo.court_name)}</a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Номер</a>
                <div className='labelInputRight'>
                    <a>{formatNull(formInfo.number)}</a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Ссылка на дело</a>
                <div className='labelInputRight'>
                    {formInfo.case_link ? <a className='aWithLink' href={`${formInfo.case_link}`}>{formInfo.case_link}</a> : <p>{formatNull(formInfo.case_link)}</p>}
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Номер входящей корреспонденции</a>
                <div className='labelInputRight'>
                    <a>{formatNull(formInfo.internal_number)}</a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Должники</a>
                <div className='labelInputRight'>
                    <CaseDebtors
                        caseId={caseId}
                        debtors_ids={formInfo.debtors || []}
                        reload={reload}
                        onSuccess={() => setReload(true)}
                        formInfo={formInfo} 
                        setFormInfo={setFormInfo}
                        isShowButtons={true}
                    />
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Заявители</a>
                <div className='labelInputRight'>
                    <CaseApplicants
                        caseId={caseId}
                        applicants_ids={formInfo.applicants || []}
                        reload={reload}
                        onSuccess={() => setReload(true)}
                        formInfo={formInfo} 
                        setFormInfo={setFormInfo}
                        isShowButtons={true}
                    />
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Дата начала учёта карточки в системе личного кабинета</a>
                <div className='labelInputRight'>
                    <a>{formatDate(formInfo.date_start)}</a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Статус карточки</a>
                <div className='labelInputRight'>
                    {formatCaseStatus(formInfo.status, 'textPill', '', 'a')}
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Файлы карточки</a>
                <div className='labelInputRight'>
                    {loading ? <LoadingBlock width="100%" height="154px" /> 
                    : <FileUploadField
                        label='Добавить файл'
                        file={files.caseFile}
                        buttonLabel='Добавить файл'
                        onChange={handleFileChange('caseFile')}
                        onReset={handleFileReset('caseFile')}
                        allowedFileTypes={['pdf', 'doc', 'docx']}
                        isAlreadyOpened={true}
                        isCloseButton={false}
                    />}
                    <FilesMiniTable 
                        file_ids={formInfo.files || []}
                        userId={formInfo.user_id}
                        isShowNullTable={true}
                        isAdmin={isAdmin}
                        deleteBan={true}
                        handleDelete={handleDeleteFile}
                        onSuccess={() => setReload(true)} 
                    />
                    {(!formInfo.application_id && !access && (formInfo.files?.length ?? 0) < 2) && (
                        <WarningPanel text={'Обязательно прикрепите согласие на назначение, если оно отсутствует в списке файлов выше'} />
                    )}
                </div>
            </div>

            <div style={{height: '20px'}}></div>

            <h3 className='formHeadline'>Заявка</h3>
            <div className='labelInputWrapper'>
                <a className='label'>Дата проведения рассылки опроса</a>
                <div className='labelInputRight'>
                    <a
                        className={(!formInfo.date_mailing && !formInfo.user_id) ? 'colorCell red' : ''}
                    >
                        {formatDate(formInfo.date_mailing)}
                        &nbsp;
                        {(!formInfo.date_mailing && !formInfo.user_id) && '[Проведите рассылку опроса или выберите управляющего]'}
                    </a>
                </div>
            </div>
            {isAdmin &&
            <div className='labelInputWrapper'>
                <a className='label'>Конечная дата предоставления согласий</a>
                <div className='labelInputRight'>
                    <a
                        className={(isDatePastInMoscow(formInfo.date_consent_deadline) && (formInfo.status=='Заявка' || formInfo.status=='В работе') && !formInfo.user_id) ? 'colorCell red' : ''}
                    >
                        {formatDate(formInfo.date_consent_deadline)}
                        &nbsp;
                        {(isDatePastInMoscow(formInfo.date_consent_deadline) && (formInfo.status=='Заявка' || formInfo.status=='В работе') && !formInfo.user_id) && '[Выберите управляющего, срок предоставления согласий управляющих истёк]'}
                    </a>
                    {!formInfo.is_consent_deadline && <a className='description'>[СРО временно продлила срок предоставления согласий]</a>}
                </div>
            </div>}
            {isAdmin &&
            <div className='labelInputWrapper'>
                <a className='label'>Дата выбора управляющего СРО</a>
                <div className='labelInputRight'>
                    <a
                        className={((!formInfo.date_candidate_selection && (formInfo.status=='Заявка' || formInfo.status=='В работе') && !formInfo.user_id) && isDatePastInMoscow(formInfo.date_candidate_selection_deadline)) ? 'colorCell red' : ''}
                    >
                        {formatDate(formInfo.date_candidate_selection)}
                        &nbsp;
                        {(!formInfo.date_candidate_selection && (formInfo.status=='Заявка' || formInfo.status=='В работе') && !formInfo.user_id) && `[Необходимо выбрать управляющего до ${formatDate(formInfo.date_candidate_selection_deadline)}]`}
                    </a>
                </div>
            </div>}
            <div className='labelInputWrapper'>
                <a className='label'>Дата направления кандидатуры в суд</a>
                <div className='labelInputRight'>
                    <a
                        className={(formInfo.date_candidate_selection && !formInfo.date_submission_candidate) ? 'colorCell red' : ''}
                    >
                        {formatDate(formInfo.date_submission_candidate)}
                        &nbsp;
                        {(isAdmin && (formInfo.date_candidate_selection && !formInfo.date_submission_candidate)) && '[Укажите дату направления кандидатуры в суд]'}
                    </a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Дата судебного заседания об утверждении АУ</a>
                <div className='labelInputRight'>
                    <a
                        className={(!formInfo.date_court && formInfo.user_id) ? 'colorCell blue' : ''}
                    >
                        {formatDate(formInfo.date_court)}
                        &nbsp;
                        {(!formInfo.date_court && formInfo.user_id) && '[Укажите дату судебного заседания об утверждении АУ]'}
                    </a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Дата утверждения управляющего АС</a>
                <div className='labelInputRight'>
                    <a className={`description ${(isDatePastInMoscow(formInfo.date_court) && !formInfo.date_am_appointment_m) ? 'colorCell red' : ''}`}>{formatDate(formInfo.date_am_appointment_m)} (полная версия)</a>
                    <a className={`description ${(isDatePastInMoscow(formInfo.date_court) && !formInfo.date_am_appointment_r) ? 'colorCell red' : ''}`}>{formatDate(formInfo.date_am_appointment_r)} (рез.часть)</a>

                    <a className={isDatePastInMoscow(formInfo.date_court) ? 'colorCell red' : ''}>{(isDatePastInMoscow(formInfo.date_court) && !formInfo.date_am_appointment_r) ? '[Дата судебного заседания прошла, необходимо указать дату утверждения управляющего АС или изменить статус дела на "Управляющий не назначен"]' : ''}</a>

                    {formInfo.status_am_appointment_approval!==null && 
                    (formInfo.status_am_appointment_approval=='Подтверждено' ?
                    <a
                        className='textPill green'
                    >
                        Назначение подтверждено СРО {formatDate(formInfo.date_am_appointment_approval)}
                    </a>
                    :
                    <a
                        className='textPill red'
                    >
                        Назначение не подтверждено СРО {formatDate(formInfo.date_am_appointment_approval)}
                    </a>)}
                </div>
            </div>

            <div style={{height: '20px'}}></div>

            <h3 className='formHeadline'>Процедура</h3>
            <div className='labelInputWrapper'>
                <a className='label'>Процедура</a>
                <div className='labelInputRight'>
                    <a>{formatNull(formInfo.procedure_name)}</a>
                </div>
            </div>
            <div className='labelInputWrapper'>
                <a className='label'>Дата введения процедуры</a>
                <div className='labelInputRight'>
                    <a className='description'>{formatDate(formInfo.date_procedure_start_m)} (полная версия)</a>
                    <a className='description'>{formatDate(formInfo.date_procedure_start_r)} (рез.часть)</a>
                </div>
            </div>
            {/* {formInfo.status=='В работе' &&
            <div className='labelInputWrapper'>
                <a className='label'>Конечный срок предоставления отчёта</a>
                <div className='labelInputRight'>
                    <a className={isDatePastInMoscow(formInfo.date_end) ? 'textPill red' : ''}>{formatDate(formInfo.date_end)} {isDatePastInMoscow(formInfo.date_end) ? '[Необходимо прикрепить отчёт]' : ''}</a>
                </div>
            </div>} */}

            <div style={{height: '20px'}}></div>

            {isAdmin && <>
            <h3 className='formHeadline'>Арбитражный управляющий</h3>
            <div className='labelInputWrapper'>
                <div style={{width: '100%'}}>
                    <AMPanel 
                        id={formInfo.id} 
                        case_creator_id={formInfo.creator_id}
                        number={formInfo.number} 
                        caseInfo={formInfo}
                        reload={reload} 
                        setReload={setReload} 
                        onSuccess={() => {setReload(true)}} 
                    />
                </div>
            </div>
            </>}
            {!isAdmin && <h3 className='formHeadline'>Связь</h3>}
            <div className='labelInputWrapper'>
                <a className='label'>Сообщения и события</a>
                <div className='labelInputRight'>
                    <CommentsCase 
                        caseId={formInfo.id} 
                        access={access} 
                        userId={formInfo.user_id} 
                        preTypeComment={preTypeComment}
                        preComment={preContentComment} 
                        reload={reload} 
                        setReload={setReload} 
                        onSuccess={() => {setReload(true)}} 
                    />
                </div>
            </div>

            {!isAdmin &&
            <>
            <div style={{height: '20px'}}></div>
            <div className='labelInputWrapper'>
                <a className='label'>Дополнительное</a>
                <div className='labelInputRight'>
                    <button 
                        type='button' 
                        className={`button miniButton`}
                        onClick={() => {
                            setPreTypeComment('appointment_refusal');
                            setPreContentComment('Я хочу отказаться от назначения');
                        }}
                        disabled={formInfo.listTypesOfMessages?.includes('appointment_refusal')}
                    >
                        Отказаться от назначения
                    </button>
                    <span className='description'>Если вы хотите отказаться от назначения, нажмите эту кнопку — система автоматически создаст и отправит сообщение о намерении. <p style={{fontWeight: '500'}}>Обязательно прикрепите необходимые документы перед отправкой.</p> Аппарат СРО получит уведомление.</span>
                </div>
            </div>
            </>}

            <div style={{height: '50px'}}></div>
        </>
        }
    </div>

    {/* <div className='pageStatus'>
        {!loading && !warning &&<>
        {(isDatePastInMoscow(formInfo.date_end) && formInfo.status=='Заявка') &&
        <WarningPanel text={'Срок исполнения заявки истёк'} type={'error'} />
        }
        {(isDatePastInMoscow(formInfo.date_end) && formInfo.status=='В работе') &&
        <WarningPanel text={'Срок предоставления отчёта вышел. Необходимо прикрепить отчёт'} />
        }
        {(!formInfo.date_court) ?
            <WarningPanel text={'Укажите дату судебного заседания о назначении'} type={''} />
            :
            (formInfo.date_court < new Date() && formInfo.user_id && !formInfo.date_am_appointment_r) &&
            <WarningPanel text={`Дата судебного заседания о назначении прошла ${formInfo.date_court}. Необходимо указать дату утверждения управляющего АС или изменить статус дела на "Управляющий не назначен"`} type={'error'} />
        }
        {(isDatePastInMoscow(formInfo.date_court) && !formInfo.date_am_appointment_r) &&
        <WarningPanel text={'Дата судебного заседания прошла, необходимо указать дату утверждения управляющего АС или изменить статус дела на "Управляющий не назначен"'} />
        }
        {(isAdmin && formInfo.user_id && !formInfo.date_submission_candidate) &&
        <WarningPanel text={'Дата направления кандидатуры в суд не указана'} type={'error'} />
        }
        {(formInfo.user_id && !formInfo.date_am_appointment_r) &&
        <WarningPanel text={'Дата утверждения управляющего АС арбитражного управляющего не указана'} type={'error'} />
        }
        {(formInfo.date_procedure_start_m && formInfo.status=='Заявка') &&
        <WarningPanel text={'Дата введения процедуры указана, но статус карточки до сих пор - "Заявка". Необходимо установить статус "В работе"'} type={'error'} />
        }
        </>}
    </div> */}
    </>
    )
}
